import { type Mask } from "react-native-mask-input";

export const UNICODE = {
  PRIME: "′",
  NBSP: "\u00A0",
  DOUBLE_PRIME: "″",
};

export const IMPERIAL_HEIGHT_MASK: Mask = (text = "") => {
  const cleanText = text.replace(/\D+/g, "");
  let inchesMask: string | RegExp = /\d/;
  if (cleanText.charAt(1) === "1") {
    inchesMask = /[01]/;
  } else {
    inchesMask = "";
  }
  return [
    /\d/,
    UNICODE.PRIME,
    UNICODE.NBSP,
    /\d/,
    inchesMask,
    UNICODE.DOUBLE_PRIME,
  ].filter(Boolean) as (string | RegExp)[];
};

/**
 * Formats a phone number with dashes (XXX-XXX-XXXX)
 * @param phoneNumber - Raw phone number string
 * @returns Formatted phone number or original if invalid
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return phoneNumber;

  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, "");

  // Apply phone mask format
  if (digitsOnly.length === 10) {
    return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  } else if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
    // Handle 1-XXX-XXX-XXXX format
    return `${digitsOnly.slice(0, 1)}-${digitsOnly.slice(1, 4)}-${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`;
  }

  // Return original if not a valid phone number length
  return phoneNumber;
};

/**
 * Formats a zip code with dash (XXXXX-XXXX for zip+4, XXXXX for 5-digit)
 * @param zipCode - Raw zip code string
 * @returns Formatted zip code or original if invalid
 */
export const formatZipCode = (zipCode: string): string => {
  if (!zipCode) return zipCode;

  // Remove all non-digit characters
  const digitsOnly = zipCode.replace(/\D/g, "");

  // Apply zip code formatting
  if (digitsOnly.length === 9) {
    // Zip+4 format: XXXXX-XXXX
    return `${digitsOnly.slice(0, 5)}-${digitsOnly.slice(5)}`;
  } else if (digitsOnly.length === 5) {
    // Standard 5-digit zip
    return digitsOnly;
  }

  // Return original if not a valid zip code length
  return zipCode;
};

/**
 * Normalizes a zip code to its leading 5 digits, stripping any formatting and
 * the ZIP+4 suffix.
 * @param zipCode - Raw zip code string
 * @returns Up to 5 leading digits
 */
export const normalizeZipCode = (zipCode: string): string =>
  zipCode.replace(/\D/g, "").slice(0, 5);

export const formatCentsAsPriceLabel = (amountInCents: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: amountInCents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amountInCents / 100);
};

export const formatAddress = (
  address?: {
    line?: string[] | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
  } | null,
): string =>
  [...(address?.line ?? []), address?.city, address?.state, address?.postalCode]
    .filter(Boolean)
    .join(", ");
